import { FormattedMessage } from 'react-intl';
import { useEffect, useRef, useState } from 'react';
import SectionTitle from '../../layout/SectionTitle';
import { Button } from '../../../common/Button';
import {
  type RouterCreateConfigurationPayload,
  sendDownloadRouterConfigurationRequest,
  sendDownloadRouterConfigurationWireGuardRequest,
  sendRouterConfigurationRequest,
} from '../../../../utils/api/routerAPI';
import { hasDefinedProps } from '../../../../utils/partialToRequired';
import { useNotificationContext } from '../../layout/FloatingNotification';
import { downloadLocalFile } from '../../../../utils/fileDownload';
import { useNavigationStop } from '../../../../utils/useNavigationStop';
import { isApiError } from '../../../../utils/api/clearApiClient';
import { grpcResponceCodes } from '../../../../utils/consts/apiCodes';
import Loader from '../../../common/Loader';
import { useActiveSectionContext } from '../../ActiveSectionContext';
import { profileSections } from '../../profileSections';
import { paramsKeys } from '../../../../utils/consts/params';
import { srollProfileToTop } from '../../profileLayout';
import RouterProtocolBlock, { type RouterProtocol } from './RouterComponents/RouterProtocolBlock';
import RouterNameBlock from './RouterComponents/RouterNameBlock';
import RouterFAQBlock from './RouterComponents/RouterFAQBlock';
import RouterConfigBlock from './RouterComponents/RouterConfigBlock';
import RouterConfigEnhancersBlock from './RouterComponents/RouterConfigEnhancersBlock';
import RouterCredentialsBlock from './RouterComponents/RouterCredentialsBlock';
import RouterDeleteBlock from './RouterComponents/RouterDeleteBlock';
import RouterDontLeaveModal from './RouterComponents/RouterDontLeaveModal';
import { protocolResponseMap } from './RouterComponents/routerConfigUtils';
import backArrow from '../../../../../assets/icons/back-black-arrow.svg';
import styles from './RouterSettingsSection.scss';

interface RouterSettingsSectionProps {
  onBackClick: (withReload?: boolean) => void;
  editedRouter: { id: string; name: string } | null;
  routerPlaceholderName: string;
  hasActiveSubscription: boolean;
  premiumLink: string;
}

const RouterSettingsSection = ({
  onBackClick,
  editedRouter,
  routerPlaceholderName,
  hasActiveSubscription,
  premiumLink,
}: RouterSettingsSectionProps) => {
  const { targetSection, setTargetSection, setActiveSection } = useActiveSectionContext();

  const [editedRouterId, setEditedRouterId] = useState(editedRouter?.id);
  const { unlockNavigation } = useNavigationStop(!editedRouterId);

  // state to determine if device list re-fetch is needed, after user leaves the page
  const [hasChanged, setHasChanged] = useState(false);

  const [isDontLeaveModalVisible, setIsDontLeaveModalVisible] = useState(false);

  const { addNotification, storeNotification } = useNotificationContext();

  const [payloadState, setPayloadState] = useState<Partial<RouterCreateConfigurationPayload>>({
    router_model: 'router',
  });

  const [isEnhancersAvailable, setIsEnhancersAvailable] = useState(false);
  const [isPremiumShortcutSelected, setIsPremiumShortcutSelected] = useState(false);

  const [selectedProtocol, setSelectedProtocol] = useState<RouterProtocol>('OpenVPN');

  const handleProtocolChange = (protocol: RouterProtocol) => {
    setSelectedProtocol(protocol);
  };

  const handleConfigChange = (
    shortcutGroupId: string,
    shortcutId: string,
    clusterId: string,
    withEnhancers: boolean,
    isPremiumShortcut: boolean,
  ) => {
    if (editedRouterId) {
      return;
    }

    setPayloadState((state) => ({
      ...state,
      shortcut_group_id: shortcutGroupId,
      shortcut_id: shortcutId,
      cluster_id: clusterId,
      enhancer: withEnhancers ? (state.enhancer ?? 'general') : 'general',
    }));
    setIsEnhancersAvailable(withEnhancers);
    setIsPremiumShortcutSelected(isPremiumShortcut);
  };

  const handleEnhancerChange = (enhancer: 'general' | 'security' | 'adblock' | 'adsec') => {
    if (isEnhancersAvailable) {
      setPayloadState((state) => ({
        ...state,
        enhancer,
      }));
    } else {
      setPayloadState((state) => ({
        ...state,
        enhancer: 'general',
      }));
    }
  };

  const isSubmitAllowed =
    Boolean(payloadState.shortcut_group_id) && Boolean(payloadState.shortcut_id) && Boolean(payloadState.cluster_id);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [existingRouterCredentials, setExistingRouterCredentials] = useState<{ username?: string; password?: string }>(
    {},
  );
  const existingConfigRef = useRef<string>(null);

  const [isLoading, setIsLoading] = useState(Boolean(editedRouter));

  useEffect(() => {
    if (!targetSection || targetSection === profileSections.DEVICES) {
      return;
    }

    if (!editedRouterId) {
      setIsDontLeaveModalVisible(true);

      return;
    }

    if (hasChanged) {
      location.href = `/profile?${paramsKeys.PROFILE_SECTION}=${targetSection}`;
    } else {
      setActiveSection(targetSection);
    }
  }, [targetSection]);

  useEffect(() => {
    if (editedRouter) {
      setIsLoading(true);
      sendRouterConfigurationRequest(editedRouter.id)
        .then((response) => {
          const protocol = protocolResponseMap[response.data.protocol ?? 'openvpn'];

          setPayloadState({
            shortcut_group_id: response.data.shortcut_group_id,
            shortcut_id: response.data.shortcut_id,
            cluster_id: response.data.cluster_id,
            enhancer: response.data.enhancer,
            router_name: editedRouter.name,
          });
          setSelectedProtocol(protocol ?? 'OpenVPN');
          setExistingRouterCredentials({ username: response.data.node_login, password: response.data.node_password });
          existingConfigRef.current = response.data.router_configuration;
          setIsLoading(false);
        })
        .catch(() => {});
    }
  }, []);

  const onSubmit = () => {
    if (isPremiumShortcutSelected && !hasActiveSubscription) {
      unlockNavigation();
      window.location.href = premiumLink;

      return;
    }

    if (editedRouterId) {
      if (!existingConfigRef.current) {
        addNotification('routerCreationError');

        return;
      }

      try {
        if (selectedProtocol === 'WireGuard') {
          downloadLocalFile(`${payloadState.router_name}_config.conf`, existingConfigRef.current);
        } else {
          downloadLocalFile(`${payloadState.router_name}_config.ovpn`, existingConfigRef.current);
        }
        addNotification('routerCreated');
      } catch {
        addNotification('routerCreationError');
      }

      return;
    }

    const payload = {
      ...payloadState,
      router_name:
        payloadState.router_name && payloadState.router_name.length > 0
          ? payloadState.router_name
          : routerPlaceholderName,
    };

    if (
      hasDefinedProps(
        payload,
        'shortcut_group_id',
        'shortcut_id',
        'cluster_id',
        'enhancer',
        'router_model',
        'router_name',
      )
    ) {
      setIsSubmitting(true);
      (selectedProtocol === 'WireGuard'
        ? sendDownloadRouterConfigurationWireGuardRequest(payload)
        : sendDownloadRouterConfigurationRequest(payload)
      )
        .then(
          (response) => {
            if (selectedProtocol === 'WireGuard') {
              downloadLocalFile(
                `${payload.router_name}_config.conf`.replaceAll(' ', '_'),
                response.router_configuration,
              );
            } else {
              downloadLocalFile(
                `${payload.router_name}_config.ovpn`.replaceAll(' ', '_'),
                response.router_configuration,
              );
            }

            setEditedRouterId(response.device_id);
            setPayloadState((p) => ({ ...p, router_name: payload.router_name }));
            setExistingRouterCredentials({ username: response.node_login, password: response.node_password });
            existingConfigRef.current = response.router_configuration;

            addNotification('routerCreated');
            setHasChanged(true);
            setIsSubmitting(false);
          },
          (error: unknown) => {
            console.error(error);
            if (
              isApiError(error) &&
              error.response?.data.code === grpcResponceCodes.INVALID_ARGUMENT &&
              error.response.data.message === 'device limit reached'
            ) {
              addNotification('deviceLimitExceeded');
            } else {
              addNotification('routerCreationError');
            }
          },
        )
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  useEffect(() => {
    srollProfileToTop();
  }, [editedRouterId]);

  if (isLoading) {
    return <Loader colored centered />;
  }

  return (
    <>
      <header>
        <Button
          kind="white"
          buttonSize="small"
          className={styles.backButton}
          onClick={() => {
            if (editedRouterId) {
              onBackClick(hasChanged);
            } else {
              setIsDontLeaveModalVisible(true);
            }
          }}
        >
          <img src={backArrow} width="16" height="16" alt="" />
          <FormattedMessage id="profile.deviceManagementSection.title" defaultMessage="Devices management" />
        </Button>
        <SectionTitle className={styles.sectionTitle}>
          {editedRouterId ? (
            <FormattedMessage id="profile.routerSection.title.existinRouter" defaultMessage="Configurations" />
          ) : (
            <FormattedMessage id="profile.routerSection.title" defaultMessage="Manual router setup" />
          )}
        </SectionTitle>
      </header>

      <RouterNameBlock
        value={payloadState.router_name ?? ''}
        editedRouterId={editedRouterId}
        onRouterRenamed={() => {
          setHasChanged(true);
        }}
        onChange={(event) => {
          setPayloadState((s) => ({ ...s, router_name: event.value }));
        }}
      />

      {existingRouterCredentials.username && existingRouterCredentials.password && (
        <>
          <hr className={styles.divider} />

          <RouterCredentialsBlock
            username={existingRouterCredentials.username}
            password={existingRouterCredentials.password}
          />
        </>
      )}

      <hr className={styles.divider} />

      <RouterProtocolBlock
        value={selectedProtocol}
        disabled={Boolean(editedRouterId)}
        onChange={handleProtocolChange}
      />

      <hr className={styles.divider} />

      <RouterConfigBlock
        protocol={selectedProtocol}
        handleConfigChange={handleConfigChange}
        disabled={Boolean(editedRouterId)}
        hasActiveSubscription={hasActiveSubscription}
        preselectedConfig={
          editedRouterId && payloadState.shortcut_group_id && payloadState.shortcut_id && payloadState.cluster_id
            ? {
                shortcut_group_id: payloadState.shortcut_group_id,
                shortcut_id: payloadState.shortcut_id,
                cluster_id: payloadState.cluster_id,
              }
            : undefined
        }
      />
      <RouterConfigEnhancersBlock
        isConfigSelected={isSubmitAllowed}
        selectedEnhancer={payloadState.enhancer}
        disabled={!isEnhancersAvailable && !isSubmitting && !isLoading}
        isExistingRouter={Boolean(editedRouterId)}
        onEnhancerChange={handleEnhancerChange}
      />

      <div className={styles.submitBlock}>
        <Button
          kind="primary"
          className={styles.submitButton}
          disabled={!isSubmitAllowed}
          loading={isSubmitting}
          onClick={onSubmit}
        >
          <FormattedMessage id="profile.routerSection.downloadButton" defaultMessage="Download config file" />
        </Button>
      </div>

      {editedRouterId && (
        <>
          <hr className={styles.divider} />
          <RouterDeleteBlock
            routerId={editedRouterId}
            onRouterDeleted={() => {
              onBackClick(true);
              storeNotification('deviceDeleted');
            }}
          />
        </>
      )}

      <hr className={styles.divider} />

      <RouterFAQBlock />

      <RouterDontLeaveModal
        isOpen={isDontLeaveModalVisible}
        onLeave={() => {
          setIsDontLeaveModalVisible(false);
          if (targetSection && targetSection !== profileSections.DEVICES) {
            setActiveSection(targetSection);
          } else {
            onBackClick();
          }
        }}
        onClose={() => {
          setIsDontLeaveModalVisible(false);
          setTargetSection(null);
        }}
      />
    </>
  );
};

export default RouterSettingsSection;
